// Centralized error types and messages for better user experience

export interface AppError {
  code: string;
  message: string;
  details?: string;
  resolution?: string;
  field?: string;
}

export const ErrorCodes = {
  // Authentication errors
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  
  // User management errors
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  INVALID_EMAIL: 'INVALID_EMAIL',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  CANNOT_DELETE_SELF: 'CANNOT_DELETE_SELF',
  
  // Assessment errors
  ASSESSMENT_NOT_FOUND: 'ASSESSMENT_NOT_FOUND',
  ASSESSMENT_NOT_EDITABLE: 'ASSESSMENT_NOT_EDITABLE',
  DUPLICATE_ASSESSMENT: 'DUPLICATE_ASSESSMENT',
  INVALID_STATUS_TRANSITION: 'INVALID_STATUS_TRANSITION',
  MISSING_REQUIRED_ANSWERS: 'MISSING_REQUIRED_ANSWERS',
  
  // Customer errors
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  DUPLICATE_CUSTOMER: 'DUPLICATE_CUSTOMER',
  CUSTOMER_HAS_DEPENDENCIES: 'CUSTOMER_HAS_DEPENDENCIES',
  
  // QBR errors
  QBR_NOT_FOUND: 'QBR_NOT_FOUND',
  QBR_GENERATION_FAILED: 'QBR_GENERATION_FAILED',
  QBR_NOT_EDITABLE: 'QBR_NOT_EDITABLE',
  
  // Template errors
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  TEMPLATE_IN_USE: 'TEMPLATE_IN_USE',
  INVALID_TEMPLATE_STRUCTURE: 'INVALID_TEMPLATE_STRUCTURE',
  
  // Budget and Findings errors
  BUDGET_NOT_FOUND: 'BUDGET_NOT_FOUND',
  FINDING_NOT_FOUND: 'FINDING_NOT_FOUND',
  
  // Integration errors
  INTEGRATION_NOT_FOUND: 'INTEGRATION_NOT_FOUND',
  INTEGRATION_AUTH_FAILED: 'INTEGRATION_AUTH_FAILED',
  SYNC_IN_PROGRESS: 'SYNC_IN_PROGRESS',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
  
  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  REQUIRED_FIELD: 'REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',
  VALUE_TOO_LONG: 'VALUE_TOO_LONG',
  VALUE_TOO_SHORT: 'VALUE_TOO_SHORT',
  
  // Database errors
  DATABASE_ERROR: 'DATABASE_ERROR',
  CONSTRAINT_VIOLATION: 'CONSTRAINT_VIOLATION',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  
  // General errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  FILE_NOT_FOUND: 'FILE_NOT_FOUND',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  
  // Additional error codes
  ORGANIZATION_NOT_FOUND: 'ORGANIZATION_NOT_FOUND',
  SECURITY_THREAT: 'SECURITY_THREAT',
  RECOMMENDATION_NOT_FOUND: 'RECOMMENDATION_NOT_FOUND',

  // Email domain (whitelabel) errors
  DOMAIN_ALREADY_CLAIMED: 'DOMAIN_ALREADY_CLAIMED',
  PROVIDER_CONFIG_ERROR: 'PROVIDER_CONFIG_ERROR',
  NO_EMAIL_DOMAIN: 'NO_EMAIL_DOMAIN',
} as const;

export const ErrorMessages: Record<string, AppError> = {
  [ErrorCodes.UNAUTHORIZED]: {
    code: ErrorCodes.UNAUTHORIZED,
    message: "You need to log in to access this feature",
    resolution: "Please sign in to your account and try again"
  },
  
  [ErrorCodes.FORBIDDEN]: {
    code: ErrorCodes.FORBIDDEN,
    message: "You don't have permission to perform this action",
    resolution: "Contact your administrator to request the necessary permissions"
  },
  
  [ErrorCodes.SESSION_EXPIRED]: {
    code: ErrorCodes.SESSION_EXPIRED,
    message: "Your session has expired",
    resolution: "Please sign in again to continue"
  },
  
  [ErrorCodes.INVALID_CREDENTIALS]: {
    code: ErrorCodes.INVALID_CREDENTIALS,
    message: "Invalid email or password",
    resolution: "Please check your credentials and try again, or reset your password if needed"
  },
  
  [ErrorCodes.USER_ALREADY_EXISTS]: {
    code: ErrorCodes.USER_ALREADY_EXISTS,
    message: "A user with this email already exists",
    resolution: "Try using a different email address or check if the user was already created"
  },
  
  [ErrorCodes.USER_NOT_FOUND]: {
    code: ErrorCodes.USER_NOT_FOUND,
    message: "User not found",
    resolution: "Please verify the user exists or try refreshing the page"
  },
  
  [ErrorCodes.INVALID_EMAIL]: {
    code: ErrorCodes.INVALID_EMAIL,
    message: "Please enter a valid email address",
    resolution: "Use the format: name@company.com"
  },
  
  [ErrorCodes.WEAK_PASSWORD]: {
    code: ErrorCodes.WEAK_PASSWORD,
    message: "Password does not meet security requirements",
    resolution: "Use at least 8 characters with uppercase, lowercase, numbers, and special characters"
  },
  
  [ErrorCodes.CANNOT_DELETE_SELF]: {
    code: ErrorCodes.CANNOT_DELETE_SELF,
    message: "You cannot delete your own account",
    resolution: "Ask another administrator to remove your account if needed"
  },
  
  [ErrorCodes.ASSESSMENT_NOT_FOUND]: {
    code: ErrorCodes.ASSESSMENT_NOT_FOUND,
    message: "Assessment not found",
    resolution: "The assessment may have been deleted or moved. Please check the assessments list"
  },
  
  [ErrorCodes.ASSESSMENT_NOT_EDITABLE]: {
    code: ErrorCodes.ASSESSMENT_NOT_EDITABLE,
    message: "This assessment cannot be edited in its current status",
    resolution: "Only draft assessments can be modified"
  },
  
  [ErrorCodes.DUPLICATE_ASSESSMENT]: {
    code: ErrorCodes.DUPLICATE_ASSESSMENT,
    message: "An assessment with this name already exists for this customer",
    resolution: "Please use a different assessment title or check existing assessments"
  },
  
  [ErrorCodes.INVALID_STATUS_TRANSITION]: {
    code: ErrorCodes.INVALID_STATUS_TRANSITION,
    message: "Invalid status change",
    resolution: "Assessments must follow the proper workflow: Draft → Pending Review → Approved"
  },
  
  [ErrorCodes.MISSING_REQUIRED_ANSWERS]: {
    code: ErrorCodes.MISSING_REQUIRED_ANSWERS,
    message: "Please complete all required questions before submitting",
    resolution: "Review the assessment and provide answers for all mandatory questions"
  },
  
  [ErrorCodes.CUSTOMER_NOT_FOUND]: {
    code: ErrorCodes.CUSTOMER_NOT_FOUND,
    message: "Customer not found",
    resolution: "The customer may have been deleted. Please check the customers list"
  },
  
  [ErrorCodes.DUPLICATE_CUSTOMER]: {
    code: ErrorCodes.DUPLICATE_CUSTOMER,
    message: "A customer with this name already exists",
    resolution: "Please use a different name or check if this customer was already added"
  },
  
  [ErrorCodes.CUSTOMER_HAS_DEPENDENCIES]: {
    code: ErrorCodes.CUSTOMER_HAS_DEPENDENCIES,
    message: "Cannot delete customer with existing assessments or QBRs",
    resolution: "Remove all assessments and QBRs for this customer first, or archive the customer instead"
  },
  
  [ErrorCodes.QBR_NOT_FOUND]: {
    code: ErrorCodes.QBR_NOT_FOUND,
    message: "QBR not found",
    resolution: "The QBR may have been deleted. Please check the QBRs list"
  },
  
  [ErrorCodes.QBR_GENERATION_FAILED]: {
    code: ErrorCodes.QBR_GENERATION_FAILED,
    message: "Failed to generate Business Review content",
    resolution: "Ensure the assessment is approved and contains sufficient data. Try again in a few moments"
  },
  
  [ErrorCodes.QBR_NOT_EDITABLE]: {
    code: ErrorCodes.QBR_NOT_EDITABLE,
    message: "This QBR cannot be edited in its current status",
    resolution: "Only draft and finalized QBRs can be modified. Presented QBRs are read-only"
  },
  
  [ErrorCodes.TEMPLATE_NOT_FOUND]: {
    code: ErrorCodes.TEMPLATE_NOT_FOUND,
    message: "Template not found",
    resolution: "The template may have been deleted. Please check the templates list"
  },
  
  [ErrorCodes.TEMPLATE_IN_USE]: {
    code: ErrorCodes.TEMPLATE_IN_USE,
    message: "Cannot delete template that is being used by existing assessments",
    resolution: "Archive the template instead, or remove all assessments using this template first"
  },
  
  [ErrorCodes.INVALID_TEMPLATE_STRUCTURE]: {
    code: ErrorCodes.INVALID_TEMPLATE_STRUCTURE,
    message: "Template structure is invalid",
    resolution: "Ensure all questions have proper titles, descriptions, and valid question types"
  },
  
  [ErrorCodes.INTEGRATION_NOT_FOUND]: {
    code: ErrorCodes.INTEGRATION_NOT_FOUND,
    message: "Integration not found",
    resolution: "The integration may have been removed. Please check your integrations settings"
  },
  
  [ErrorCodes.INTEGRATION_AUTH_FAILED]: {
    code: ErrorCodes.INTEGRATION_AUTH_FAILED,
    message: "Integration authentication failed",
    resolution: "Check your API credentials and ensure they have the required permissions"
  },
  
  [ErrorCodes.SYNC_IN_PROGRESS]: {
    code: ErrorCodes.SYNC_IN_PROGRESS,
    message: "A sync operation is already in progress",
    resolution: "Please wait for the current sync to complete before starting another"
  },
  
  [ErrorCodes.EXTERNAL_SERVICE_ERROR]: {
    code: ErrorCodes.EXTERNAL_SERVICE_ERROR,
    message: "External service is temporarily unavailable",
    resolution: "Please try again in a few minutes. If the problem persists, contact support"
  },
  
  [ErrorCodes.VALIDATION_ERROR]: {
    code: ErrorCodes.VALIDATION_ERROR,
    message: "Please correct the validation errors",
    resolution: "Review the highlighted fields and provide valid information"
  },
  
  [ErrorCodes.REQUIRED_FIELD]: {
    code: ErrorCodes.REQUIRED_FIELD,
    message: "This field is required",
    resolution: "Please provide a value for this field"
  },
  
  [ErrorCodes.INVALID_FORMAT]: {
    code: ErrorCodes.INVALID_FORMAT,
    message: "Invalid format",
    resolution: "Please check the expected format and try again"
  },
  
  [ErrorCodes.VALUE_TOO_LONG]: {
    code: ErrorCodes.VALUE_TOO_LONG,
    message: "Value is too long",
    resolution: "Please shorten your input to the maximum allowed length"
  },
  
  [ErrorCodes.VALUE_TOO_SHORT]: {
    code: ErrorCodes.VALUE_TOO_SHORT,
    message: "Value is too short",
    resolution: "Please provide a longer value that meets the minimum requirements"
  },
  
  [ErrorCodes.DATABASE_ERROR]: {
    code: ErrorCodes.DATABASE_ERROR,
    message: "Database operation failed",
    resolution: "Please try again. If the problem persists, contact technical support"
  },
  
  [ErrorCodes.CONSTRAINT_VIOLATION]: {
    code: ErrorCodes.CONSTRAINT_VIOLATION,
    message: "Data constraint violation",
    resolution: "Please check for duplicate entries or missing required relationships"
  },
  
  [ErrorCodes.TIMEOUT_ERROR]: {
    code: ErrorCodes.TIMEOUT_ERROR,
    message: "Operation timed out",
    resolution: "The operation took too long. Please try again or try with less data"
  },
  
  [ErrorCodes.INTERNAL_ERROR]: {
    code: ErrorCodes.INTERNAL_ERROR,
    message: "An unexpected error occurred",
    resolution: "Please try again. If the problem persists, contact technical support"
  },
  
  [ErrorCodes.NETWORK_ERROR]: {
    code: ErrorCodes.NETWORK_ERROR,
    message: "Network connection error",
    resolution: "Please check your internet connection and try again"
  },
  
  [ErrorCodes.FILE_NOT_FOUND]: {
    code: ErrorCodes.FILE_NOT_FOUND,
    message: "File not found",
    resolution: "The file may have been moved or deleted. Please check the file path"
  },
  
  [ErrorCodes.PERMISSION_DENIED]: {
    code: ErrorCodes.PERMISSION_DENIED,
    message: "Permission denied",
    resolution: "You don't have the required permissions. Contact your administrator for access"
  },
  
  [ErrorCodes.ORGANIZATION_NOT_FOUND]: {
    code: ErrorCodes.ORGANIZATION_NOT_FOUND,
    message: "Organization not found",
    resolution: "The organization may have been deleted. Please check your organization settings"
  },
  
  [ErrorCodes.SECURITY_THREAT]: {
    code: ErrorCodes.SECURITY_THREAT,
    message: "Security threat detected",
    resolution: "This action has been blocked for security reasons. Contact support if you believe this is an error"
  },
  
  [ErrorCodes.RECOMMENDATION_NOT_FOUND]: {
    code: ErrorCodes.RECOMMENDATION_NOT_FOUND,
    message: "Recommendation not found",
    resolution: "The recommendation may have been deleted. Please check the recommendations list"
  },

  [ErrorCodes.DOMAIN_ALREADY_CLAIMED]: {
    code: ErrorCodes.DOMAIN_ALREADY_CLAIMED,
    message: "This domain is already authenticated by another organization",
    resolution: "Use a different sending domain, or contact support if you believe this is an error"
  },

  [ErrorCodes.PROVIDER_CONFIG_ERROR]: {
    code: ErrorCodes.PROVIDER_CONFIG_ERROR,
    message: "Email provider is not configured for domain authentication",
    resolution: "The email provider API key is missing the required permissions. Please contact support"
  },

  [ErrorCodes.NO_EMAIL_DOMAIN]: {
    code: ErrorCodes.NO_EMAIL_DOMAIN,
    message: "No email domain is configured",
    resolution: "Set up a sending domain before verifying or removing it"
  },
};

export function createError(code: keyof typeof ErrorCodes, customMessage?: string, details?: string, field?: string): AppError {
  const baseError = ErrorMessages[code] || ErrorMessages[ErrorCodes.INTERNAL_ERROR];
  
  return {
    ...baseError,
    message: customMessage || baseError.message,
    details,
    field,
  };
}

export function formatErrorForUser(error: AppError): string {
  let message = error.message;
  
  if (error.details) {
    message += `: ${error.details}`;
  }
  
  if (error.resolution) {
    message += `\n\nHow to resolve: ${error.resolution}`;
  }
  
  return message;
}